/**
 * Trip P&L — classic desk math. Grok does not invent these figures.
 *
 * Charter economics: occupied + positioning. Variable = measured fuel, trip
 * fees, and hourly Mx + engine reserves. A set variable-DOC component on the
 * type or tail replaces the company reserve. Fuel planning on that table does
 * not replace measured fuel. Operator-sim — not GAAP.
 */

import { hydrateCurrency, type CurrencyCode, type MoneyCatalogs } from "./catalogs";
import {
  isEmptyFerry,
  isRevenueCharter,
  ofpPlannedBlockHours,
  quoteCharter,
  type CharterQuote,
  type QuoteOpts,
} from "./ledger";
import { parkingDaysFromDwellHours, resolveParkingDwell } from "./parkingDwell";
import { quoteHotelForOvernight } from "./hotelOvernight";
import { icaoKey } from "./curfew";
import type { SimPhaseSample } from "./simDraft";
import { findTripTelemetry, tripFuelUsedGal, tripHoursForQuote, type TailTripTelemetry } from "./simTelemetry";
import { ofpPlannedFuelGal } from "./tripFuel";
import { DATA_SOURCE_LABEL } from "./dataSource";
import type {
  AircraftSpec,
  CompanyAccount,
  FlightLogEntry,
  LedgerTxn,
  LedgerTxnKind,
  Mission,
  OpsDeskSettings,
  RateTables,
} from "./types";

export type MoneyBasis = "estimate" | "actual";
export type MoneySource = "measured" | "table" | "assumption";

export type TripPnlLine = {
  kind: string;
  memo: string;
  amountCad: number;
  basis: MoneyBasis;
  source: MoneySource;
  missionId?: string;
};

export type TripPnlBucket = {
  amountCad: number;
  basis: MoneyBasis;
  source: MoneySource;
};

export type LinkedFerryPnl = {
  missionId: string;
  route: string;
  fuelCad: number;
  feesCad: number;
  reserveCad: number;
  totalCad: number;
  basis: MoneyBasis;
};

export type TripPnl = {
  currency: CurrencyCode;
  charterId: string;
  revenue: TripPnlBucket;
  fuel: TripPnlBucket;
  landing: TripPnlBucket;
  handling: TripPnlBucket;
  parking: TripPnlBucket;
  hotel: TripPnlBucket;
  ferryCosts: TripPnlBucket;
  ferries: LinkedFerryPnl[];
  mxReserve: TripPnlBucket;
  engineReserve: TripPnlBucket;
  costsCad: number;
  netCad: number;
  lines: TripPnlLine[];
  /** True when any line was taken from a posted ledger row. */
  posted: boolean;
};

export function ferriesForCharter(
  missions: ReadonlyArray<Mission> | undefined,
  charter: Pick<Mission, "id" | "inboxRequestId">,
): Mission[] {
  if (!missions?.length) return [];
  return missions.filter((m) => {
    if (m.id === charter.id) return false;
    if (!isEmptyFerry(m)) return false;
    if (m.parentMissionId && m.parentMissionId === charter.id) return true;
    if (charter.inboxRequestId && m.inboxRequestId === charter.inboxRequestId) return true;
    return false;
  });
}

function emptyBucket(): TripPnlBucket {
  return { amountCad: 0, basis: "estimate", source: "table" };
}

function addBucket(current: TripPnlBucket, add: number, basis: MoneyBasis, source: MoneySource): TripPnlBucket {
  const amountCad = Math.round((current.amountCad + add) * 100) / 100;
  const nextBasis: MoneyBasis = current.amountCad === 0 ? basis : current.basis === "actual" && basis === "actual" ? "actual" : current.basis === basis ? basis : "estimate";
  const nextSource: MoneySource =
    source === "measured" || current.source === "measured"
      ? "measured"
      : source === "assumption" || current.source === "assumption"
        ? "assumption"
        : "table";
  return { amountCad, basis: current.amountCad === 0 ? basis : nextBasis, source: nextSource };
}

function postedForMission(account: CompanyAccount | undefined, missionId: string): LedgerTxn[] {
  if (!account || !missionId) return [];
  return account.transactions.filter((t) => t.missionId === missionId);
}

function amountOfKind(rows: LedgerTxn[], kind: LedgerTxnKind): number | undefined {
  const hit = rows.filter((t) => t.kind === kind);
  if (!hit.length) return undefined;
  return Math.round(hit.reduce((s, t) => s + t.amountCad, 0) * 100) / 100;
}

function logFor(logs: ReadonlyArray<FlightLogEntry> | undefined, mission: Mission): FlightLogEntry | undefined {
  return logs?.find((l) => l.tripId === mission.id || l.id === mission.id);
}

function actualFuelGal(log?: FlightLogEntry): number | undefined {
  const gal = log?.arrival?.fuelUsedGal;
  return typeof gal === "number" && gal > 0 ? gal : undefined;
}

function quoteOpts(
  catalogs: MoneyCatalogs | undefined,
  aircraft: AircraftSpec | undefined,
  log?: FlightLogEntry,
  home?: CurrencyCode,
  parkingDays?: number,
  extra?: { simFuelGal?: number; ofpPlannedFuelGal?: number; simHours?: number; ofpPlannedHours?: number },
  opsDesk?: OpsDeskSettings | null,
): QuoteOpts {
  return {
    catalogs,
    aircraft,
    homeCurrency: home ?? catalogs?.currency,
    actualFuelGal: actualFuelGal(log),
    simFuelGal: extra?.simFuelGal,
    ofpPlannedFuelGal: extra?.ofpPlannedFuelGal,
    simHours: extra?.simHours,
    ofpPlannedHours: extra?.ofpPlannedHours,
    parkingDays,
    opsDesk,
  };
}

function extrasFromTelemetry(
  mission: Mission,
  tel?: TailTripTelemetry | null,
): { simFuelGal?: number; ofpPlannedFuelGal?: number; simHours?: number; ofpPlannedHours?: number } {
  return {
    simFuelGal: tripFuelUsedGal(tel) ?? undefined,
    ofpPlannedFuelGal: ofpPlannedFuelGal(mission.ofpAttachment) ?? undefined,
    simHours: tripHoursForQuote(tel) ?? undefined,
    ofpPlannedHours: ofpPlannedBlockHours(mission),
  };
}

function aircraftFor(mission: Mission, fleet?: ReadonlyArray<AircraftSpec>): AircraftSpec | undefined {
  return fleet?.find((a) => a.id === mission.aircraftId || a.registration === mission.registration);
}

function bucketFromPostedOrQuote(
  posted: number | undefined,
  quoted: number,
  sign: 1 | -1,
  measured: boolean,
): TripPnlBucket {
  if (posted != null) {
    return { amountCad: posted, basis: "actual", source: measured ? "measured" : "table" };
  }
  const amountCad = Math.round(quoted * sign * 100) / 100;
  return {
    amountCad,
    basis: measured ? "actual" : "estimate",
    source: measured ? "measured" : "table",
  };
}

function reserveBucket(posted: number | undefined, quoted: number): TripPnlBucket {
  if (posted != null) return { amountCad: posted, basis: "actual", source: "assumption" };
  return { amountCad: Math.round(-quoted * 100) / 100, basis: "estimate", source: "assumption" };
}

function lineFromBucket(
  kind: string,
  memo: string,
  bucket: TripPnlBucket,
  missionId?: string,
): TripPnlLine | null {
  if (!bucket.amountCad) return null;
  return { kind, memo, amountCad: bucket.amountCad, basis: bucket.basis, source: bucket.source, missionId };
}

function ferryPnl(
  ferry: Mission,
  rates: RateTables,
  catalogs: MoneyCatalogs | undefined,
  fleet: ReadonlyArray<AircraftSpec> | undefined,
  logs: ReadonlyArray<FlightLogEntry> | undefined,
  account: CompanyAccount | undefined,
  home: CurrencyCode,
  tripTelemetry?: TailTripTelemetry[] | TailTripTelemetry | null,
  opsDesk?: OpsDeskSettings | null,
): { ferry: LinkedFerryPnl; fuel: TripPnlBucket; fees: TripPnlBucket; mx: TripPnlBucket; engine: TripPnlBucket } {
  const log = logFor(logs, ferry);
  const aircraft = aircraftFor(ferry, fleet);
  const tel = Array.isArray(tripTelemetry)
    ? findTripTelemetry(tripTelemetry, { tripId: ferry.id, registration: ferry.registration })
    : tripTelemetry?.tripId === ferry.id || tripTelemetry?.registration === ferry.registration
      ? tripTelemetry
      : undefined;
  const quote = quoteCharter(
    ferry,
    rates,
    log,
    quoteOpts(catalogs, aircraft, log, home, undefined, extrasFromTelemetry(ferry, tel), opsDesk),
  );
  const posted = postedForMission(account, ferry.id);
  const fuelMeasured = actualFuelGal(log) != null || tripFuelUsedGal(tel) != null;
  const fuel = bucketFromPostedOrQuote(amountOfKind(posted, "fuel"), quote.fuelCad, -1, fuelMeasured);
  const landing = bucketFromPostedOrQuote(amountOfKind(posted, "landing"), quote.landingCad, -1, false);
  const handling = bucketFromPostedOrQuote(amountOfKind(posted, "handling"), quote.handlingCad, -1, false);
  const parking = bucketFromPostedOrQuote(amountOfKind(posted, "parking"), quote.parkingCad, -1, false);
  const mx = reserveBucket(amountOfKind(posted, "mx_reserve"), quote.mxReserveCad);
  const engine = reserveBucket(amountOfKind(posted, "engine_reserve"), quote.engineReserveCad);
  const feesCad = Math.round((landing.amountCad + handling.amountCad + parking.amountCad) * 100) / 100;
  const reserveCad = Math.round((mx.amountCad + engine.amountCad) * 100) / 100;
  // Ferry "cost" on the charter P&L is fuel + airport fees. Hourly reserves
  // roll into the trip reserve lines (occupied + positioning hours).
  const totalCad = Math.round((fuel.amountCad + feesCad) * 100) / 100;
  const anyPosted = posted.some((t) =>
    t.kind === "fuel" || t.kind === "landing" || t.kind === "handling" || t.kind === "parking" || t.kind === "mx_reserve" || t.kind === "engine_reserve",
  );
  return {
    ferry: {
      missionId: ferry.id,
      route: `${ferry.originIcao}–${ferry.destIcao}`,
      fuelCad: fuel.amountCad,
      feesCad,
      reserveCad,
      totalCad,
      basis: anyPosted || fuelMeasured ? "actual" : "estimate",
    },
    fuel,
    fees: { amountCad: feesCad, basis: anyPosted ? "actual" : "estimate", source: "table" },
    mx,
    engine,
  };
}

export function buildTripPnl(input: {
  mission: Mission;
  missions?: Mission[];
  logs?: FlightLogEntry[];
  account?: CompanyAccount;
  rates: RateTables;
  catalogs?: MoneyCatalogs;
  fleet?: AircraftSpec[];
  currency?: CurrencyCode;
  simHistory?: SimPhaseSample[];
  tripTelemetry?: TailTripTelemetry[] | TailTripTelemetry | null;
  /** Variable DOC. Set Mx / engine replace the company reserve on the quote. */
  opsDesk?: OpsDeskSettings | null;
}): TripPnl {
  const mission = input.mission;
  const home = hydrateCurrency(input.currency ?? input.catalogs?.currency ?? input.account?.homeCurrency);
  const log = logFor(input.logs, mission);
  const aircraft = aircraftFor(mission, input.fleet);
  const dwell = input.logs
    ? resolveParkingDwell({
        mission,
        logs: input.logs,
        missions: input.missions,
        simHistory: input.simHistory,
      })
    : null;
  const parkingDays = dwell ? parkingDaysFromDwellHours(dwell.hours) : undefined;
  const tel = Array.isArray(input.tripTelemetry)
    ? findTripTelemetry(input.tripTelemetry, { tripId: mission.id, registration: mission.registration })
    : input.tripTelemetry;
  const quote: CharterQuote = quoteCharter(
    mission,
    input.rates,
    log,
    quoteOpts(input.catalogs, aircraft, log, home, parkingDays, extrasFromTelemetry(mission, tel), input.opsDesk),
  );
  const posted = postedForMission(input.account, mission.id);
  const fuelMeasured = actualFuelGal(log) != null || tripFuelUsedGal(tel) != null;
  const fuelSourceLabel = DATA_SOURCE_LABEL[quote.fuelSource];

  const revenue = isRevenueCharter(mission)
    ? bucketFromPostedOrQuote(amountOfKind(posted, "charter_revenue"), quote.revenueCad, 1, false)
    : emptyBucket();
  if (!isRevenueCharter(mission)) {
    revenue.source = "table";
    revenue.basis = "actual";
  }

  const fuel = bucketFromPostedOrQuote(amountOfKind(posted, "fuel"), quote.fuelCad, -1, fuelMeasured);
  const landing = bucketFromPostedOrQuote(amountOfKind(posted, "landing"), quote.landingCad, -1, false);
  const handling = bucketFromPostedOrQuote(amountOfKind(posted, "handling"), quote.handlingCad, -1, false);
  const parkingMeasured = dwell != null;
  const parking = parkingMeasured
    ? {
        amountCad: Math.round(-quote.parkingCad * 100) / 100,
        basis: "actual" as const,
        source: "measured" as const,
      }
    : bucketFromPostedOrQuote(amountOfKind(posted, "parking"), quote.parkingCad, -1, false);
  const hotelPosted = amountOfKind(posted, "hotel");
  const hotelIcao = icaoKey(mission.overnightAtIcao);
  const hotelOnThisArrival = Boolean(hotelIcao && icaoKey(mission.destIcao) === hotelIcao);
  const hotelQuote = hotelOnThisArrival
    ? quoteHotelForOvernight({ icao: hotelIcao, homeIcao: "", dwell })
    : { hotelCad: 0, nights: 0, rateCad: 0 };
  // Hotel is ops after overnight sticks — never invent it on a quote-only trip.
  // When dest === overnight field, show the planning figure until posted.
  const hotel =
    hotelPosted != null
      ? { amountCad: hotelPosted, basis: "actual" as const, source: "table" as const }
      : hotelOnThisArrival && hotelQuote.hotelCad > 0
        ? { amountCad: Math.round(-hotelQuote.hotelCad * 100) / 100, basis: "estimate" as const, source: "table" as const }
        : emptyBucket();
  let mxReserve = reserveBucket(amountOfKind(posted, "mx_reserve"), quote.mxReserveCad);
  let engineReserve = reserveBucket(amountOfKind(posted, "engine_reserve"), quote.engineReserveCad);

  const linked = ferriesForCharter(input.missions, mission);
  const ferries: LinkedFerryPnl[] = [];
  let ferryCosts = emptyBucket();
  for (const ferry of linked) {
    const row = ferryPnl(
      ferry,
      input.rates,
      input.catalogs,
      input.fleet,
      input.logs,
      input.account,
      home,
      input.tripTelemetry,
      input.opsDesk,
    );
    ferries.push(row.ferry);
    ferryCosts = addBucket(ferryCosts, row.ferry.totalCad, row.ferry.basis, "table");
    mxReserve = addBucket(mxReserve, row.mx.amountCad, row.mx.basis, "assumption");
    engineReserve = addBucket(engineReserve, row.engine.amountCad, row.engine.basis, "assumption");
  }

  const lines: TripPnlLine[] = [];
  const push = (row: TripPnlLine | null) => {
    if (row) lines.push(row);
  };
  push(lineFromBucket("charter_revenue", `Revenue ${mission.originIcao}–${mission.destIcao}`, revenue, mission.id));
  push(lineFromBucket("fuel", `Fuel ${mission.originIcao} (${fuelSourceLabel})`, fuel, mission.id));
  push(lineFromBucket("landing", `Landing ${mission.destIcao}`, landing, mission.id));
  push(lineFromBucket("handling", `Handling ${mission.destIcao}`, handling, mission.id));
  push(lineFromBucket("parking", `Parking ${mission.destIcao}`, parking, mission.id));
  push(lineFromBucket("hotel", `Crew hotel ${mission.overnightAtIcao || mission.destIcao}`, hotel, mission.id));
  for (const f of ferries) {
    if (f.totalCad) {
      lines.push({
        kind: "ferry",
        memo: `Positioning ${f.route}`,
        amountCad: f.totalCad,
        basis: f.basis,
        source: "table",
        missionId: f.missionId,
      });
    }
  }
  push(lineFromBucket("mx_reserve", "Mx reserve (hours)", mxReserve, mission.id));
  push(lineFromBucket("engine_reserve", "Engine reserve (hours)", engineReserve, mission.id));

  const costsCad = Math.round(
    (fuel.amountCad + landing.amountCad + handling.amountCad + parking.amountCad + hotel.amountCad + ferryCosts.amountCad) * 100,
  ) / 100;
  // Reserves are costs too (variable hour accrual) — include in net, listed separately.
  const netCad = Math.round((revenue.amountCad + costsCad + mxReserve.amountCad + engineReserve.amountCad) * 100) / 100;
  const allCosts = Math.round((costsCad + mxReserve.amountCad + engineReserve.amountCad) * 100) / 100;

  return {
    currency: home,
    charterId: mission.id,
    revenue,
    fuel,
    landing,
    handling,
    parking,
    hotel,
    ferryCosts,
    ferries,
    mxReserve,
    engineReserve,
    costsCad: allCosts,
    netCad,
    lines,
    posted: posted.length > 0 || linked.some((f) => postedForMission(input.account, f.id).length > 0),
  };
}

export function formatPnlBasis(basis: MoneyBasis): string {
  return basis === "actual" ? "Actual" : "Estimate";
}
